import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MmDialogService, MmDialogSize, MmMenuTrigger } from '@metromarkets/components';
import { Store } from '@ngrx/store';
import isEmpty from 'lodash.isempty';
import { filter, take } from 'rxjs/operators';

import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { OrderLineActionsVisibility } from '../../classes/order-line-actions-visibility';
import { DocumentTypes } from '../../constants';
import { OrderLine } from '../../models';
import { DocumentFile } from '../../models/document-file.model';
import { OrderHistoryDialogConfigService } from '../../services/order-history-dialog-config.service';
import { OrdersHistoryService } from '../../services/orders-history.service';
import {
  cancelOrderLine,
  cancelOrderLineReturnRequest,
  OrdersHistoryState,
  returnOrderLineRequest,
} from '../../store/orders-history-list';
import { DocumentDialogComponent } from '../documents-dialog/document-dialog.component';
import { OrderConfirmDialogComponent } from '../order-confirm-dialog/order-confirm-dialog.component';
import { OrderReturnInstructionsComponent } from '../order-return-instructions/order-return-instructions.component';
import { ReturnOrderLineComponent } from '../return-order-line/return-order-line.component';
import { ReceiveDocumentGetLink } from '../../store/receive-document-link';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { OrderHistoryApplicationEvent } from '~shared/modules/application-event/models';
import { GaTrackingEventName } from '~shared/modules/ga-tracking/constants';
import { OrderHistoryEventActionType } from '~shared/modules/application-event/constants';
import { APP_PRODUCT_ROUTE } from '~app/modules/account/modules/orders-history/constants/routes.constant';

@Component({
  selector: 'app-order-line-actions-bar',
  templateUrl: './order-line-actions-bar.component.html',
  styleUrls: ['./order-line-actions-bar.component.scss'],
  providers: [OrderHistoryDialogConfigService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderLineActionsBarComponent extends OrderLineActionsVisibility {
  @Input() orderId: string;
  @Input() orderLine: OrderLine;
  @Input() isContactEmailFeatureEnabled: boolean;
  @Input() isGetInvoiceFeatureEnabled: boolean;
  @Input() isMobileOrTablet: boolean;
  @Input() isSubsidiaryAccount:boolean;
  
  APP_PRODUCT_ROUTE = APP_PRODUCT_ROUTE;

  public readonly menuTrigger = MmMenuTrigger;
  public readonly docTypes = DocumentTypes;

  constructor(private mmDialogService: MmDialogService,
              private store$: Store<OrdersHistoryState>,
              private featureFlagSrv: ActiveFeaturesConfigcatService,
              private ordersHistoryService: OrdersHistoryService,
              private dialogConfigSrv: OrderHistoryDialogConfigService,
              private applicationEventService: ApplicationEventService) {
    super();
  }

  public cancelOrderLine(): void {
    const dialogConfig = this.dialogConfigSrv.getCancelOrderLineConfig(this.orderLine.product.name);

    this.mmDialogService.open(OrderConfirmDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(take(1), filter(Boolean))
      .subscribe(() => {
        const payload = {
          orderLineId: this.orderLine.orderLineId,
          orderId: this.orderId,
        };
        this.store$.dispatch(cancelOrderLine({ payload }));

        this.applicationEventService.publish(new OrderHistoryApplicationEvent({
          event: GaTrackingEventName.CancelProduct,
          action: OrderHistoryEventActionType.CancelProduct,
          label: this.orderLine.orderLineId,
        }));
      });
  }

  public openReturnInstructionsDialog(): void {
    this.mmDialogService.open(OrderReturnInstructionsComponent, {
      dialogSize: MmDialogSize.large,
    });
  }

  public returnProduct(): void {
    const orderlineData = {
      orderId: this.orderId,
      orderLine: this.orderLine
    };
    this.mmDialogService.open(ReturnOrderLineComponent, { data: orderlineData })
      .afterClosed()
      .pipe(take(1), filter((value) => !isEmpty(value)))
      .subscribe((values) => {
        this.store$.dispatch(returnOrderLineRequest({
          payload: {
            ...values,
            orderLineId: this.orderLine.orderLineId,
            orderId: this.orderId,
          },
        }));

        this.applicationEventService.publish(new OrderHistoryApplicationEvent({
          event: GaTrackingEventName.ReturnProduct,
          action: OrderHistoryEventActionType.ReturnProduct,
          label: this.orderLine.orderLineId,
        }));
      });
  }

  public cancelReturn(): void {
    const dialogConfig = this.dialogConfigSrv.cancelReturningConfig;
    const payload = { orderId: this.orderId, orderLineId: this.orderLine.orderLineId };

    this.mmDialogService.open(OrderConfirmDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(take(1), filter(Boolean))
      .subscribe(() => this.store$.dispatch(cancelOrderLineReturnRequest({ payload })));
  }

  public downloadInvoices() {
    const { invoices, creditNotes } = this.orderLine.documents;
    const documents = [...invoices, ...creditNotes];

    this.downloadFiles(documents, this.docTypes.INVOICE);

    this.applicationEventService.publish(new OrderHistoryApplicationEvent({
      event: GaTrackingEventName.GetInvoices,
      action: OrderHistoryEventActionType.DownLoadInvoice,
      label: this.orderLine.orderLineId,
    }));
  }

  public downloadFiles(documents: DocumentFile[], docType: DocumentTypes): void {
    if (documents.length === 1) {
      const [document] = documents;
      const { id, type, name } = document;

      this.store$.dispatch(new ReceiveDocumentGetLink({ id, name, type }));
    } else {
      this.mmDialogService.open(DocumentDialogComponent, {
        data: { files: documents, type: docType },
        dialogSize: MmDialogSize.large,
      });
    }
  }
}
